import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function NavigationBar({ isPublic = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (error) {
      console.error('Sign out error:', error);
      setSigningOut(false);
    }
  };

  const isActive = (path) => {
    return location?.pathname === path;
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={isPublic ? "/" : "/admin/dashboard"} className="flex items-center space-x-3">
            <img 
              src="/assets/images/GetPromptLogo-1770953730661.png" 
              alt="GetPrompt Logo" 
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold text-gray-900">GetPrompt</span>
          </Link>

          {/* Navigation Tabs - Only show for authenticated admin users */}
          {!isPublic && user && (
            <div className="hidden md:flex space-x-1">
              <Link
                to="/admin/dashboard"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/admin/dashboard') || isActive('/admin')
                    ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/admin/generator"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/admin/generator')
                    ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                Generator
              </Link>
              <Link
                to="/admin/use-case-management"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/admin/use-case-management')
                    ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                Management
              </Link>
              <Link
                to="/admin/analytics"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/admin/analytics')
                    ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                Analytics
              </Link>
            </div>
          )}

          {/* User Menu - Only show for authenticated users */}
          {!isPublic && user && (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.email?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-600 transition-transform ${
                    showUserMenu ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                    <p className="text-xs text-gray-500 mt-1">Administrator</p>
                  </div>
                  <Link
                    to="/user-profile-settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowUserMenu(false)}
                  >
                    Profile Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {signingOut ? 'Signing out...' : 'Sign Out'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}